import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  LogOut,
  ArrowLeftRight,
  ChevronUp,
  ChevronRight,
  User,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import CustomerNotificationBanner from '../user/CustomerNotificationBanner';

// Shared shell for the Admin and Staff consoles (AdminLayout / StaffLayout pass
// their own menu and branding).
//
// Every console route renders its own layout, so it remounts on each
// navigation. Caching the account and approvals count at module level lets the
// sidebar render the right menu immediately instead of flashing the wrong one
// while the fetch is in flight. Cleared on sign-out.
let cachedAccount = null;
let cachedPendingApprovals = 0;

// Sign-out can happen outside this layout (e.g. the customer sidebar), so clear
// the cache on any auth change that isn't a token refresh for the same user.
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || (cachedAccount && session?.user?.id !== cachedAccount.id)) {
    cachedAccount = null;
    cachedPendingApprovals = 0;
  }
});

// Menu shape: [{ group: string|null, items: [{ path, icon, label, exact?,
// match?: [extra path prefixes that also highlight this item], badge?: 'approvals' }] }]
const itemMatches = (item, pathname) => {
  if (item.exact) return pathname === item.path;
  return [item.path, ...(item.match || [])].some(
    (p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`)
  );
};

/**
 * @param menu      groups of items (see shape above)
 * @param canSee    (item, account) => boolean — role filter for items
 * @param getBrand  (account) => { title, subtitle, homePath, rootLabel }
 */
const ConsoleLayout = ({ children, menu, canSee = () => true, getBrand }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(cachedAccount);
  const [pendingApprovals, setPendingApprovals] = useState(cachedPendingApprovals);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountMenuRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the drawer and account menu when navigating.
  useEffect(() => {
    setSidebarOpen(false);
    setShowAccountMenu(false);
  }, [location.pathname]);

  // Close the account menu on outside click or Escape.
  useEffect(() => {
    if (!showAccountMenu) return undefined;
    const onPointerDown = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setShowAccountMenu(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowAccountMenu(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showAccountMenu]);

  const groups = currentUser
    ? menu.map((g) => ({ ...g, items: g.items.filter((item) => canSee(item, currentUser)) }))
        .filter((g) => g.items.length > 0)
    : [];

  // Live pending-approvals count for the nav badge, when a visible item shows it.
  // Subscribes to all order changes rather than filtering on pending_approval,
  // because an approval moves the row *out* of that filter and wouldn't be delivered.
  const showApprovalsBadge = groups.some((g) => g.items.some((item) => item.badge === 'approvals'));
  useEffect(() => {
    if (!showApprovalsBadge) return undefined;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('orders')
        .select('id', { count: 'exact', head: true })
        .eq('internal_status', 'pending_approval');
      if (error) {
        console.error('Error fetching pending approvals count:', error);
        return;
      }
      cachedPendingApprovals = count || 0;
      setPendingApprovals(cachedPendingApprovals);
    };
    fetchCount();
    const channel = supabase
      .channel(`console_nav_approvals_${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, fetchCount)
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [showApprovalsBadge]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        cachedAccount = data;
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      showError(error, 'Failed to fetch user data');
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      cachedAccount = null;
      cachedPendingApprovals = 0;
      showNotification('Successfully signed out!', 'success');
      history.push('/login');
    } catch (error) {
      showError(error, 'Failed to sign out');
    }
  };

  const badges = { approvals: pendingApprovals };

  // Header breadcrumb: the group and label of the current menu item.
  const current = groups
    .flatMap((g) => g.items.map((item) => ({ ...item, group: g.group })))
    .find((item) => itemMatches(item, location.pathname));

  const brand = getBrand(currentUser);

  // Called as a function (not rendered as <SidebarContent />) so React doesn't
  // treat it as a new component each render and reset the nav's scroll position.
  const renderSidebar = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-16 border-b border-white/10 flex-shrink-0">
        <button
          onClick={() => history.push(brand.homePath)}
          className="flex items-center min-w-0 rounded-lg p-1 -m-1 hover:bg-white/5 transition-colors"
        >
          <img src="/Logo2.png" alt="MHS" className="h-8 w-auto flex-shrink-0" />
          <div className="ml-3 min-w-0 text-left">
            <p className="text-sm font-semibold text-white truncate">{brand.title}</p>
            <p className="text-xs text-gray-400 truncate">{brand.subtitle}</p>
          </div>
        </button>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden text-gray-400 hover:text-white"
          aria-label="Close sidebar"
        >
          <X className="w-6 h-6" aria-hidden="true" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto min-h-0" aria-label={`${brand.rootLabel} navigation`}>
        {!currentUser ? (
          <div className="space-y-2 px-1" aria-hidden="true">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-8 rounded-lg bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.group || 'top'}>
              {g.group && (
                <h3 className="px-3 mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {g.group}
                </h3>
              )}
              <div className="space-y-0.5">
                {g.items.map((item) => {
                  const Icon = item.icon;
                  const active = itemMatches(item, location.pathname);
                  const count = item.badge ? badges[item.badge] : 0;
                  return (
                    <button
                      key={item.path}
                      onClick={() => history.push(item.path)}
                      aria-current={active ? 'page' : undefined}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
                        active
                          ? 'bg-brand text-brand-dark'
                          : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-3 flex-shrink-0" aria-hidden="true" />
                      <span className="flex-1 text-left truncate">{item.label}</span>
                      {count > 0 && (
                        <span
                          className={`ml-2 min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold leading-none flex items-center justify-center tabular-nums ${
                            active ? 'bg-brand-dark text-white' : 'bg-brand text-brand-dark'
                          }`}
                          aria-label={`${count} pending`}
                        >
                          {count > 99 ? '99+' : count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 p-3 border-t border-white/10 space-y-2">
        <button
          onClick={() => history.push('/user/dashboard')}
          className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-200 border border-white/15 hover:bg-white/10 hover:text-white rounded-lg transition-colors duration-150"
        >
          <ArrowLeftRight className="w-4 h-4 mr-3" aria-hidden="true" />
          Back to Customer Portal
        </button>

        <div className="relative" ref={accountMenuRef}>
          {showAccountMenu && (
            <div
              role="menu"
              className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50"
            >
              <button
                role="menuitem"
                onClick={() => history.push('/account')}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <User className="w-4 h-4 mr-3 text-gray-500" aria-hidden="true" />
                Account Overview
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button
                role="menuitem"
                onClick={handleSignOut}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700"
              >
                <LogOut className="w-4 h-4 mr-3" aria-hidden="true" />
                Sign Out
              </button>
            </div>
          )}
          <button
            onClick={() => setShowAccountMenu((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={showAccountMenu}
            className="w-full flex items-center px-3 py-2 rounded-lg hover:bg-white/10 transition-colors duration-150"
          >
            <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm text-white font-semibold">
                {currentUser?.full_name?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="ml-3 flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-white truncate">{currentUser?.full_name || 'User'}</p>
              <p className="text-xs text-gray-400 truncate">{currentUser?.email}</p>
            </div>
            <ChevronUp
              className={`w-4 h-4 text-gray-400 transition-transform ${showAccountMenu ? '' : 'rotate-180'}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64 h-screen bg-brand-dark">
          {renderSidebar()}
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="md:hidden">
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-brand-dark overflow-hidden">
            {renderSidebar()}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar: breadcrumb only — each page renders its own title */}
        <div className="relative z-10 flex-shrink-0 flex items-center h-16 bg-white border-b border-gray-200">
          <button
            onClick={() => setSidebarOpen(true)}
            className="h-full px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand md:hidden"
            aria-label="Open sidebar"
            aria-expanded={sidebarOpen}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
          <nav className="flex-1 px-4 sm:px-6 md:px-8 flex items-center text-sm min-w-0" aria-label="Breadcrumb">
            <span className="text-gray-500 flex-shrink-0">{brand.rootLabel}</span>
            {current?.group && (
              <>
                <ChevronRight className="w-4 h-4 mx-1.5 text-gray-300 flex-shrink-0" aria-hidden="true" />
                <span className="text-gray-500 flex-shrink-0">{current.group}</span>
              </>
            )}
            {current && (
              <>
                <ChevronRight className="w-4 h-4 mx-1.5 text-gray-300 flex-shrink-0" aria-hidden="true" />
                <span className="font-medium text-gray-900 truncate" aria-current="page">{current.label}</span>
              </>
            )}
          </nav>
        </div>

        {/* Page Content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <CustomerNotificationBanner />
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ConsoleLayout;
